/**
 * @file seo-patch.ts
 * @description Значения формы «Сайт → SEO → Основное» и тело
 * `PATCH /admin/site/seo` из правок карточки (phase19.16, партия Р3в,
 * v0.19.64; SB145; контракт r3v-api-contract §5): только изменённые поля,
 * строки — без пробелов по краям (пусто → `null`; окончание заголовков —
 * как есть, с ведущим пробелом, SB88), картинка — id медиа,
 * коды — без пустых строк и повторов. Чистые функции — проверяют тесты.
 */

import type { ShopSeoFieldKey, ShopSeoImage, ShopSeoPatch, ShopSeoSettingsDto } from "../api-site-seo";
import type { SiteMedia } from "../site-view/types";

/** Значения полей в форме: картинка — объект медиатеки (для превью и `MediaField`). */
export interface ShopSeoFormValues {
  defaultTitle: string | null;
  titleSuffix: string | null;
  description: string | null;
  ogImageMediaId: SiteMedia | null;
  appShortName: string | null;
  allowIndexing: boolean;
  yandexVerification: string[];
  googleVerification: string[];
  /** v0.19.65 (SEO8): IndexNow; сервер до v0.19.65 блока не отдаёт — «включено» (умолчание). */
  indexNowEnabled: boolean;
}

type PatchFields = Omit<ShopSeoPatch, "expectedVersion" | "confirm" | "acknowledgeChecks">;

/** Картинка ответа → объект медиатеки для `MediaField`. */
export function seoImageMedia(image: ShopSeoImage | null): SiteMedia | null {
  return image ? { id: image.id, url: image.url, mimeType: "image/*", width: image.width, height: image.height, altText: image.alt } : null;
}

/** Значения формы по ответу сервера. */
export function seoFormValues(settings: ShopSeoSettingsDto): ShopSeoFormValues {
  return {
    defaultTitle: settings.defaultTitle,
    titleSuffix: settings.titleSuffix,
    description: settings.description,
    ogImageMediaId: seoImageMedia(settings.ogImage),
    appShortName: settings.appShortName,
    allowIndexing: settings.allowIndexing,
    yandexVerification: settings.verification.yandex,
    googleVerification: settings.verification.google,
    indexNowEnabled: settings.indexNow?.enabled ?? true,
  };
}

const text = (value: string | null): string | null => {
  const trimmed = (value ?? "").trim();
  return trimmed ? trimmed : null;
};

/** Окончание заголовков хранится как есть, с разделителем « — Магазин» (SB88): пробелы не режем, пустое → null. */
const verbatim = (value: string | null): string | null => ((value ?? "").trim() ? (value as string) : null);

const codes = (list: readonly string[]): string[] => [...new Set(list.map((c) => c.trim()).filter(Boolean))];

/** Как поле уходит на сервер (реестр: поле → запись в тело). */
const WRITERS: { [K in ShopSeoFieldKey]: (value: ShopSeoFormValues[K]) => PatchFields } = {
  defaultTitle: (v) => ({ defaultTitle: text(v) }),
  titleSuffix: (v) => ({ titleSuffix: verbatim(v) }),
  description: (v) => ({ description: text(v) }),
  ogImageMediaId: (v) => ({ ogImageMediaId: v?.id ?? null }),
  appShortName: (v) => ({ appShortName: text(v) }),
  allowIndexing: (v) => ({ allowIndexing: v }),
  yandexVerification: (v) => ({ yandexVerification: codes(v) }),
  googleVerification: (v) => ({ googleVerification: codes(v) }),
  indexNowEnabled: (v) => ({ indexNowEnabled: v }),
};

function write<K extends ShopSeoFieldKey>(key: K, value: ShopSeoFormValues[K]): PatchFields {
  return WRITERS[key](value);
}

/** Сравнимый вид значения: строки без краёв, картинка — id, коды — без пустых. */
function comparable<K extends ShopSeoFieldKey>(key: K, value: ShopSeoFormValues[K]): string {
  return JSON.stringify(Object.values(write(key, value))[0] ?? null);
}

/** Отличается ли значение поля от сервера (с учётом пробелов и пустых кодов). */
export function seoValueChanged<K extends ShopSeoFieldKey>(key: K, next: ShopSeoFormValues[K], server: ShopSeoFormValues[K]): boolean {
  return comparable(key, next) !== comparable(key, server);
}

/** Тело правки по списку изменённых полей. */
export function seoPatchOf<K extends ShopSeoFieldKey>(values: Pick<ShopSeoFormValues, K>, keys: readonly K[]): PatchFields {
  return keys.reduce<PatchFields>((patch, key) => ({ ...patch, ...write(key, values[key]) }), {});
}
